import { motion } from 'framer-motion';
import { ArrowRight, HardHat } from 'lucide-react';

const HeroSection = () => {
  const handleScrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToServices = (e) => {
    e.preventDefault();
    const element = document.querySelector('#servicios');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="inicio" 
      className="relative min-h-screen flex items-center"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/4170184/pexels-photo-4170184.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        {/* Overlay - más oscuro para look industrial */}
        <div className="absolute inset-0 bg-[#1a1a1a]/80"></div>
        {/* Línea decorativa */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2E75B6]"></div>
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            {/* Overline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center gap-3 mb-8"
            >
              <div className="w-12 h-[2px] bg-[#2E75B6]"></div>
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#7F7F7F]">
                Constructora de Vanguardia
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 leading-[1.1]"
            >
              Construyendo el
              <span className="block text-[#2E75B6]">Futuro Urbano</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-white/70 mb-10 max-w-lg leading-relaxed"
            >
              Especialistas en infraestructura de alta densidad, vivienda multifamiliar 
              y complejos comerciales. Transformamos visiones en estructuras que perduran.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#contacto"
                onClick={handleScrollToContact}
                className="inline-flex items-center justify-center gap-3 bg-[#2E75B6] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#203864] transition-all duration-300 group"
                data-testid="hero-cta-primary"
              >
                Solicitar Cotización
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#servicios"
                onClick={handleScrollToServices}
                className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-white/10 transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                <HardHat size={18} />
                Ver Servicios
              </a>
            </motion.div>
          </motion.div>

          {/* Right side - Stats card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:block"
          >
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8">
              <p className="text-white/50 text-sm uppercase tracking-wider mb-4">Especialización</p>
              <ul className="space-y-4">
                {['Vivienda Multifamiliar', 'Inmuebles Comerciales', 'Edificación Institucional', 'Urbanización'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white/80">
                    <div className="w-2 h-2 bg-[#2E75B6]"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/40 text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-white/40 to-transparent"></div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
